<script setup>
/**
 * One thing the assistant may do to a chat, beyond replying.
 *
 * TWO COMMIT POINTS ON ONE CARD, AND THAT IS DELIBERATE.
 *
 *   • THE SWITCH WRITES ON ITS OWN, immediately, sending `{ enabled }` and nothing else.
 *     Turning a permission off is the thing an owner does in a hurry — the assistant just
 *     closed a chat that was not finished — and making her press Save afterwards means the
 *     one control that has to be instant is the one that is not. It is safe only because
 *     the BFF's PATCH is genuinely partial: the switch cannot carry a half-typed note or a
 *     stale list of names along with it.
 *
 *   • THE NOTE AND THE PICKED NAMES WAIT FOR THE PAGE'S ONE SAVE BAR, because they are
 *     text and ticks in progress; writing on every keystroke would post fifty times and
 *     race itself. The card has NO Save of its own (P-5.1: switches save the moment they
 *     are flipped, typed text saves with the page's bar). It reports its draft upwards
 *     through `dirty` and the page commits it.
 *
 * WHAT THE CARD REFUSES TO SEND: `chosen`, whenever the chooser is not `ready`. That is the
 * whole of the fix for the wipe the old UI needed three mechanisms to survive — see
 * `AllowlistEditor.vue`, state 5.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';

import Switch from 'dashboard/components-next/switch/Switch.vue';

import AllowlistEditor from './AllowlistEditor.vue';
import ContactFieldsEditor from './ContactFieldsEditor.vue';
import WasloTextArea from './WasloTextArea.vue';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** One row from `wasloApi.getPermissions`. */
  permission: { type: Object, required: true },
  /** `'loading' | 'ready' | 'unreadable'` — only meaningful when `needsChoices`. */
  targetsState: { type: String, default: 'loading' },
  targets: { type: Object, default: null },
  /** True while this card's own switch is being written. */
  isToggling: { type: Boolean, default: false },
  /**
   * Bumped by the page when a switch write fails. `Switch` flips its own copy on click
   * whether or not the server agreed, so remounting it is the only reliable way to snap it
   * back to what is actually stored — the same fix, for the same reason, as `TaskCard`.
   */
  switchEpoch: { type: Number, default: 0 },
  /** A message from the last failed write on THIS card. */
  saveError: { type: String, default: '' },
});

/**
 * `dirty` carries the patch this card would send, or `null` when it has nothing waiting.
 * It is what lets the PAGE know this card is unsaved — the save bar, the leave guard and
 * the closing-tab warning all read one combined state — and what lets the page's Save
 * commit the card without reaching into it. Emitted on every change, so what the section
 * holds is never a stale draft.
 */
const emit = defineEmits(['toggle', 'retry-targets', 'dirty']);

const { t } = useWasloI18n();

const key = name => `WASLO.PERSONALITY.PERMISSIONS.${name}`;

const copy = computed(() => {
  const name = props.permission.key.toUpperCase();
  return {
    label: t(`${key('ITEMS')}.${name}.LABEL`),
    body: t(`${key('ITEMS')}.${name}.BODY`),
  };
});

// ── the draft ──────────────────────────────────────────────────────────────────────
// Local copies, re-synced from the server row FIELD BY FIELD — see the watcher below for
// the rule, which is the whole of W-10. A write answers with the SAVED row, so adopting it
// is what keeps the card showing what is stored rather than what was typed.
// A copy, never the prop's own arrays: the chooser replaces them as it is ticked, and
// mutating what came from the parent is how a "cancel" stops being possible.
const cloneChosen = value => ({
  teams: value.teams.map(team => ({ id: team.id, name: team.name })),
  agents: value.agents.map(agent => ({ id: agent.id, name: agent.name })),
  labels: [...value.labels],
  // Lead capture's two lists. `?? []` because a row from a BFF older than the `contact`
  // permission carries neither, and "none chosen" is literally true of it.
  contactFields: (value.contactFields ?? []).map(field => ({
    ...field,
    ...(field.values ? { values: [...field.values] } : {}),
  })),
  contactLabels: [...(value.contactLabels ?? [])],
});

const isContact = computed(() => props.permission.key === 'contact');

/**
 * The lists THIS card edits, and the only ones it ever sends: the contact card sends its
 * two, the assign and labels cards the three they always sent. So a BFF older than lead
 * capture — whose `chosen` schema is strict — never receives a key it would refuse.
 */
const pickChosen = value => {
  const clone = cloneChosen(value);
  return isContact.value
    ? { contactFields: clone.contactFields, contactLabels: clone.contactLabels }
    : { teams: clone.teams, agents: clone.agents, labels: clone.labels };
};

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const guidance = ref(props.permission.guidance);
const chosen = ref(cloneChosen(props.permission.chosen));

/**
 * RE-SYNC ONLY WHAT THE OWNER HAS NOT TOUCHED, AND COMPARE AGAINST THE PREVIOUS PROP.
 *
 * This watcher used to take the whole row — `guidance.value = next.guidance` — every time
 * the parent replaced it. The parent replaces it on every SWITCH, because the switch
 * renders from the response. So flipping a card's switch while she was half way through
 * writing its note threw the note and the ticks away, silently, with nothing to undo it
 * with. A partner lost a round of edits that way (W-10).
 *
 * "Has she touched it" is a comparison against `prev`, the row this card was showing when
 * she started typing — NOT against `next`, which is the answer that just arrived. Compared
 * against `next` a save would look like an edit and the card would never settle. So:
 *
 *   • local === prev  → she never touched it. Take what the server now says.
 *   • local !== prev  → it is her draft. Keep it, and leave it dirty so she can commit it.
 *
 * Which means a field she IS editing does not follow the server. That is the trade, and it
 * is the right way round: what is on the server can be read again, and what she typed and
 * has not saved cannot be recovered from anywhere.
 */
watch(
  () => props.permission,
  (next, prev) => {
    if (!prev || guidance.value === prev.guidance) {
      guidance.value = next.guidance;
    }
    // Through `pickChosen` on both sides, which puts the same lists in the same top-level
    // order on each. `same` is `JSON.stringify`, so everything BELOW that — the order of
    // the entries in each list, and the key order inside a `contactFields` row — still has
    // to match for this to read "untouched". It does today because every layer preserves
    // order: the BFF echoes what it stored, `cloneChosen` maps in place, and the choosers
    // append rather than re-sort. If one of those ever re-orders, this stops adopting a
    // list she has not touched (it keeps her copy) — safe, but stale.
    if (!prev || same(pickChosen(chosen.value), pickChosen(prev.chosen))) {
      chosen.value = cloneChosen(next.chosen);
    }
  }
);

// A chooser that is not `ready` has nothing to compare: what is on screen is what is
// stored, by construction, so it can never be dirty.
const choicesAreEditable = computed(
  () => props.permission.needsChoices && props.targetsState === 'ready'
);

const guidanceChanged = computed(
  () => guidance.value.trim() !== props.permission.guidance.trim()
);

const choicesChanged = computed(
  () =>
    choicesAreEditable.value &&
    !same(pickChosen(chosen.value), pickChosen(props.permission.chosen))
);

const isDirty = computed(() => guidanceChanged.value || choicesChanged.value);

/** Read from the BFF, never re-derived — like `inert` itself. */
const inertMessage = computed(() =>
  props.permission.inertReason === 'service_account_missing'
    ? t('WASLO.PERSONALITY.PERMISSIONS.CONTACT_DETAILS.NO_SERVICE_ACCOUNT')
    : t('WASLO.PERSONALITY.PERMISSIONS.CHOICES.INERT')
);

/**
 * What the page's Save bar writes for this card (P-5.1: notes and ticks commit only through
 * the one bar; the switch still writes on its own, instantly).
 */
const pendingPatch = computed(() => {
  const patch = {};
  if (guidanceChanged.value) patch.guidance = guidance.value.trim();
  // THE ONE LINE THAT REPLACES THE OLD UI'S THREE GUARDS.
  if (choicesChanged.value) patch.chosen = pickChosen(chosen.value);
  return patch;
});

// The page above has to know this card is unsaved — see `dirty` on the emits. Not
// `immediate`: a card mounts holding exactly what was handed to it, so there is nothing to
// announce until something changes.
watch(
  () => (isDirty.value ? pendingPatch.value : null),
  patch => emit('dirty', patch)
);

// A card that leaves the screen takes its draft with it. Leaving the key behind would dock
// a "not saved yet" bar over a card nobody can see, press or commit.
onBeforeUnmount(() => emit('dirty', null));
</script>

<template>
  <section
    class="flex flex-col gap-5 p-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
    :data-waslo-permission="permission.key"
    :data-waslo-permission-enabled="permission.enabled"
  >
    <div class="flex items-start justify-between gap-6">
      <div class="flex flex-col min-w-0 gap-1">
        <h3 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ copy.label }}
        </h3>
        <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
          {{ copy.body }}
        </p>
      </div>
      <div class="flex items-center gap-3 shrink-0">
        <span class="text-sm text-n-slate-11" data-waslo-permission-state>
          {{
            isToggling
              ? t(key('SAVING'))
              : permission.enabled
                ? t(key('ON'))
                : t(key('OFF'))
          }}
        </span>
        <Switch
          :key="switchEpoch"
          :model-value="permission.enabled"
          :aria-label="copy.label"
          data-waslo-permission-switch
          @update:model-value="next => emit('toggle', next)"
        />
      </div>
    </div>

    <!--
      On, needs a pick, nothing picked. The assistant is never even offered the action, so
      the card has to say so where the switch is — a permission that reads "On" and cannot
      fire is the single most misleading state on this screen. `inert` is computed on the
      BFF from the same rule the engine applies.
    -->
    <p
      v-if="permission.inert"
      class="flex items-start gap-2 mb-0 text-sm leading-6 text-n-amber-11"
      data-waslo-permission-inert
    >
      <span
        class="mt-1 shrink-0 size-4 i-lucide-triangle-alert"
        aria-hidden="true"
      />
      {{ inertMessage }}
    </p>

    <WasloTextArea
      :model-value="guidance"
      :label="t('WASLO.PERSONALITY.PERMISSIONS.GUIDANCE.LABEL')"
      :placeholder="t('WASLO.PERSONALITY.PERMISSIONS.GUIDANCE.PLACEHOLDER')"
      :hint="t('WASLO.PERSONALITY.PERMISSIONS.GUIDANCE.HINT')"
      :max-length="2000"
      min-height="3.5rem"
      data-waslo-permission-guidance
      @update:model-value="value => (guidance = value)"
    />

    <ContactFieldsEditor
      v-if="isContact"
      :state="targetsState"
      :targets="targets"
      :chosen="chosen"
      @update:chosen="value => (chosen = value)"
      @retry="emit('retry-targets')"
    />
    <AllowlistEditor
      v-else-if="permission.needsChoices"
      :permission-key="permission.key"
      :state="targetsState"
      :targets="targets"
      :chosen="chosen"
      @update:chosen="value => (chosen = value)"
      @retry="emit('retry-targets')"
    />

    <p
      v-if="saveError"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-permission-error
    >
      {{ saveError }}
    </p>
  </section>
</template>
